import { SectionTitle } from '@web_opxp/components';
import { useTranslation } from '@web_opxp/features';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  CELEBRATION_ACHIEVEMENT_BADGES_WAITING_MS,
  CELEBRATION_ACHIEVEMENT_BADGE_TIMEOUT_MS,
  CELEBRATION_BADGE_TIMEOUT_MS,
  CELEBRATION_LOTTIE_TIMEOUT_MS,
} from '../constants';
import * as Selectors from '../selectors';
import { AchievementsActions } from '../slice';
import { EAchievementsIntencity, IAchievement } from '../types';
import { getRandomTitle } from '../utils';
import AchievementBadgesList from './AchievementBadgesList';
import CelebrationBadge from './CelebrationBadge';
import CelebrationLottie from './CelebrationLottie';
import './CelebrationView.scss';

export interface ICelebrationViewProps {
  achievements: IAchievement[];
  intensity: EAchievementsIntencity;
  show: boolean;
  exerciseId: string;
  score: number;
  onHide: () => void;
}

export const CelebrationViewFC: React.FC<ICelebrationViewProps> = ({
  achievements,
  intensity,
  show,
  exerciseId,
  score,
  onHide,
}) => {
  const { t } = useTranslation();
  const [showCelebration, setShowCelebration] = useState(false);
  const [showLottie, setShowLottie] = useState(false);
  const animationTimeout = useRef<any>();
  const [shrinkBadge, setShrinkBadge] = useState(false);
  const [showList, setShowList] = useState(false);
  const clearAnimationTimeout = useCallback(() => {
    clearTimeout(animationTimeout.current);
    animationTimeout.current = undefined;
  }, []);
  // hide view and invoke callback
  const hideCelebrationView = useCallback(() => {
    setShowCelebration(false);
    clearAnimationTimeout();
    onHide();
  }, [clearAnimationTimeout, setShowCelebration, onHide]);
  // show list and wait for hiding
  const startAddAchievements = useCallback(() => {
    setShowList(true);
    const timeoutToHideView =
      CELEBRATION_ACHIEVEMENT_BADGE_TIMEOUT_MS * achievements.length + CELEBRATION_ACHIEVEMENT_BADGES_WAITING_MS;
    animationTimeout.current = setTimeout(hideCelebrationView, timeoutToHideView);
  }, [achievements, hideCelebrationView, setShowList]);
  // stop showing lottie and shrink the badge to the left
  const stopLottie = useCallback(() => {
    setShowLottie(false);
    // start next step
    setShrinkBadge(true);
    animationTimeout.current = setTimeout(startAddAchievements, CELEBRATION_BADGE_TIMEOUT_MS);
  }, [setShowLottie, startAddAchievements]);
  // start waiting for lottie to end
  useEffect(() => {
    if (!showCelebration) {
      return;
    }
    if (animationTimeout.current) {
      clearAnimationTimeout();
    }
    setShowLottie(true);
    animationTimeout.current = setTimeout(stopLottie, CELEBRATION_LOTTIE_TIMEOUT_MS);
    return clearAnimationTimeout;
  }, [clearAnimationTimeout, showCelebration, stopLottie]);
  // trigger showing the component when show flag is set
  useEffect(() => {
    setShowCelebration(show);
  }, [show]);
  const title = useMemo(() => {
    return t(getRandomTitle(intensity));
  }, [intensity, t]);
  if (!showCelebration) {
    return null;
  }
  const titleClasses = 'celebration-title';
  const lottieName = `Fireworks_${intensity}`;
  const shrinkBadgeClass = shrinkBadge ? ' shrink' : '';
  const badgeAnimationClass = `badge-animation-container${shrinkBadgeClass}`;
  return (
    <div id="celebration-view" className="view">
      <SectionTitle className={titleClasses}>{title}</SectionTitle>
      <div className="celebration-container">
        <div className={badgeAnimationClass}>
          <CelebrationBadge exerciseId={exerciseId} score={score} />
        </div>
        <AchievementBadgesList achievements={achievements} show={showList} />
      </div>
      <CelebrationLottie name={lottieName} show={showLottie} />
    </div>
  );
};

export const mapStateToProps = (state: Selectors.IAppState) => {
  const loading = Selectors.isLoading(state);
  const exerciseId = Selectors.getCurrentExerciseId(state);
  const score = Selectors.getCurrentScore(state);
  const achievements = Selectors.getCurrentAchievements(state);
  const show = !loading && achievements.length > 0;

  let intensity: EAchievementsIntencity;

  if (achievements.length > 2) {
    intensity = EAchievementsIntencity.Outstanding;
  } else if (achievements.length > 1) {
    intensity = EAchievementsIntencity.Great;
  } else {
    intensity = EAchievementsIntencity.Good;
  }

  return {
    // limit achievements show by 10 to not overflow the container
    achievements: achievements.slice(0, 10),
    intensity,
    show,
    exerciseId,
    score,
  };
};

const mapDispatchToProps = {
  onHide: AchievementsActions.hideAchievements,
};

const CelebrationView = connect(mapStateToProps, mapDispatchToProps)(CelebrationViewFC);
CelebrationView.displayName = 'CelebrationView';

export default CelebrationView;
